import React, { useState, useEffect } from "react";
import { Box, Flex, Heading, Text, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import useThemeColors from "../../../../../hooks/useThemeColors";
import { textSecondary } from "../../../../../utils/colors";
import SaveButtons from "./SaveButtons";
import { useDispatch, useSelector } from "react-redux";
import { TENANT_UPDATE_CLINIC_DETAILS_RESET } from "../../../../../constants/tenantConstants";
import FormControlPassword from "../../../../../components/input/FormControlPassword";
import { changePasswordAction } from "../../../../../actions/userActions";
import { USER_CHANGE_PASSWORD_RESET } from "../../../../../constants/userConstants";

const UserProfilePassword = ({ slug, tenant, userInfo }) => {
  // GENERAL
  // const { slug } = useParams();
  const { t } = useTranslation();
  const toast = useToast();

  // COLORS
  const { whiteMode, backgroundLightMode, textPrimaryMode, newBorderMode } =
    useThemeColors();

  // LANGUAGE
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  // STATE
  const [oldPassword, setOldPassword] = useState("");

  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");

  const [showButtons, setShowButtons] = useState(false);

  // const [clinicName, setClinicName] = useState(tenant?.clinicName || "");
  const [email, setEmail] = useState(userInfo?.email || "");
  const [supportEmail, setSupportEmail] = useState(tenant?.supportEmail || "");
  const [phone, setPhone] = useState(tenant?.phone || "");
  const [website, setWebsite] = useState(tenant?.website || "");

  const [name, setName] = useState(userInfo?.name || "");

  const [address, setAddress] = useState(() => ({
    street: tenant?.address?.street || "",
    city: tenant?.address?.city || "",
    country: tenant?.address?.country || "",
    zip: tenant?.address?.zip || "",
    state: tenant?.address?.state || "",
    display: tenant?.address
      ? `${tenant.address.street}, ${tenant.address.city}, ${tenant.address.zip}`
      : "",
  }));

  const [manualAddressVisible, setManualAddressVisible] = useState(false);

  // REDUX
  const dispatch = useDispatch();

  const tenantUpdateClinicDetails = useSelector(
    (state) => state.tenantUpdateClinicDetails
  );
  const {
    loading: loadingUpdateClinicDetails,
    error: errorUpdateClinicDetails,
    success: successUpdateClinicDetails,
  } = tenantUpdateClinicDetails;

  const userChangePassword = useSelector((state) => state.userChangePassword);
  const {
    loading: loadingChangePassword,
    error: errorChangePassword,
    success: successChangePassword,
  } = userChangePassword;

  // HANDLERS
  const changePasswordHandler = () => {
    console.log("Changing password for user:", {
      email: userInfo?.email,
      oldPassword,
      password,
    }); // ✅ Debugging log

    if (password !== repeatPassword) {
      toast({
        title: t("toasts.registerErrorPasswordsDoNotMatch"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    dispatch(
      changePasswordAction(slug, email, currentLanguage, oldPassword, password)
    );
  };

  const updateClinicDetailsHandler = () => {
    console.log("User details to be sent:", {
      name,
      supportEmail,
      phone,
      address,
    }); // ✅ Debugging log

    const userDetails = {
      name,
      supportEmail,
      phone,
      address,
    };

    // dispatch(
    //   updateTenantClinicDetailsAction(slug, tenant.tenantID, clinicDetails)
    // );
  };

  const toggleManualAddressVisible = () => {
    setManualAddressVisible(!manualAddressVisible);
  };

  // Handler for when an address is selected
  const handleAddressSelect = (selectedAddress) => {
    setAddress({
      street: selectedAddress.street,
      city: selectedAddress.city,
      country: selectedAddress.country,
      zip: selectedAddress.zip,
      state: selectedAddress.state,
      display: `${selectedAddress.street}, ${selectedAddress.city}, ${selectedAddress.zip}`,
    });
  };

  // USE EFFECT

  const normalizeValue = (value) =>
    value === undefined || value === null ? "" : String(value).trim();

  const normalizePhone = (phone) =>
    phone === undefined || phone === null || phone === "" || phone === "+370"
      ? ""
      : phone;

  useEffect(() => {
    if (
      oldPassword.trim() !== "" ||
      password.trim() !== "" ||
      repeatPassword.trim() !== ""
    ) {
      setShowButtons(true);
    } else {
      setShowButtons(false);
    }
  }, [oldPassword, password, repeatPassword]);

  useEffect(() => {
    if (successUpdateClinicDetails) {
      toast({
        title: t("toasts.clinicDetailsUpdated"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setShowButtons(false);
    } else if (errorUpdateClinicDetails) {
      toast({
        title: t("toasts.clinicDetailsUpdatedError"),
        status: "error",
        duration: 3000,

        isClosable: true,
      });
    }

    dispatch({ type: TENANT_UPDATE_CLINIC_DETAILS_RESET });
  }, [successUpdateClinicDetails, errorUpdateClinicDetails]);

  useEffect(() => {
    if (successChangePassword) {
      toast({
        title: t("toasts.passwordChangeSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setOldPassword("");
      setPassword("");
      setRepeatPassword("");
    } else if (errorChangePassword) {
      console.error("Error changing password:", errorChangePassword);
      if (errorChangePassword === "Current password is incorrect") {
        toast({
          title: t("toasts.currentPasswordIncorrect"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("toasts.passwordChangeError"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
    // Reset the change password state
    dispatch({ type: USER_CHANGE_PASSWORD_RESET });
  }, [successChangePassword, errorChangePassword]);

  // const isGoogleAccount = userInfo?.googleAccount || false;

  // LOGS
  // console.log("tenant", tenant);
  // console.log("showButtons", showButtons);
  // console.log("clinicName", clinicName);
  // console.log("registrationEmail", registrationEmail);
  // console.log("supportEmail", supportEmail);
  // console.log("phone", phone);
  // console.log("address", address);
  // console.log("isGoogleAccount", isGoogleAccount);
  // console.log("currentLanguage", currentLanguage);

  return (
    <Box
      w="100%"
      p="3rem 0rem 3rem"
      borderBottom={`1px solid ${newBorderMode}`}
    >
      <Flex w="100%" justify="space-between" gap="2rem">
        {/* LEFT START */}
        <Flex direction="column" gap={2} w="40%">
          <Heading fontSize="1rem" fontWeight="600">
            {t("user.userPassword")}
          </Heading>
          <Text color={textSecondary} fontSize="sm" fontWeight="500">
            {t("user.userPasswordDescription")}
          </Text>
        </Flex>
        {/* LEFT END */}

        {/* RIGHT START */}
        <Flex
          w="800px"
          direction="column"
          justify="flex-start"
          align="flex-start"
        >
          <Flex
            direction="column"
            w="100%"
            bg={backgroundLightMode}
            borderRadius="1rem"
            p="1.25rem 1.5rem 1.25rem"
          >
            <FormControlPassword
              labelFontSize="0.8rem"
              size="lg"
              label={t("form.oldPassword")}
              value={oldPassword}
              isRequired
              placeholder={t("placeholder.oldPassword")}
              // error={errors.password}
              // errorMessage={errors.password}
              onChange={(e) => {
                setOldPassword(e.target.value);
              }}
              isLabelEnabled
            />

            <FormControlPassword
              labelFontSize="0.8rem"
              size="lg"
              label={t("form.password")}
              value={password}
              isRequired
              placeholder={t("placeholder.password")}
              // error={errors.password}
              // errorMessage={errors.password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              isLabelEnabled
            />

            <Flex w="100%">
              <FormControlPassword
                labelFontSize="0.8rem"
                size="lg"
                label={t("form.repeatPassword")}
                value={repeatPassword}
                isRequired
                placeholder={t("placeholder.repeatPassword")}
                // error={errors.password}
                // errorMessage={errors.password}
                onChange={(e) => {
                  setRepeatPassword(e.target.value);
                }}
                isLabelEnabled
              />
            </Flex>
          </Flex>
        </Flex>
        {/* RIGHT END */}
      </Flex>

      {/* BUTTONS */}
      {showButtons && (
        <SaveButtons
          onClose={() => {
            setOldPassword("");
            setPassword("");
            setRepeatPassword("");
            setShowButtons(false);
          }}
          onClick={changePasswordHandler}
          loading
        />
      )}
    </Box>
  );
};

export default UserProfilePassword;
